import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faRepeat, faUserCheck, faScissors, faEye } from '@fortawesome/free-solid-svg-icons';
import {
  useNotifications,
  StatusBadge,
  createTextColumn,
  createDateColumn,
  createEnumColumn,
  createCustomColumn,
  useTableState,
  useColumnSettings,
  ActionButton,
  OutOfOfficeMark,
  outOfOfficeForAssignee,
} from '@medprompt/shared';
import { StatusEnum } from '../enums/statusEnum';
import { RiskCategoryEnum } from '../enums/riskCategory';
import NotificationPreview from '../components/NotificationPreview';
import { DocumentSourceEnum, resolveDocumentSource } from '../detail/DocumentSourceEnum';
import { saveDocumentNavigation } from '../detail/documentNavigationSession';
import { getRowActions } from './useDocumentRowActions';

/** Hidden column keys until the user saves column settings — must match modal `defaultHidden`. */
export const DOCUMENTS_TABLE_DEFAULT_HIDDEN_COLUMNS = ['organisation', 'documentTitle', 'pageCount', 'letterDate', 'documentSource', 'filedAwayAt'];

/**
 * Columns, table state, selection and bulk actions for the documents table, plus the two ends of
 * detail navigation: opening a row with its prev/next context, and highlighting the row on return.
 */
const useDocumentTable = ({ list, patientNames, rowActions, viewerContext }) => {
  const { hasViewers } = viewerContext || {};
  const { documentService, refreshDocuments, getAssigneeName, activeUsers } = list;
  const { nhsIdToPatientData } = patientNames;
  const { handleRegenerateClick, handleDeleteClick, handleSplitExistingDocument, openPreview } = rowActions;
  const { addNotification } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    sortState: tableSortState,
    setSortState: setTableSortState,
    filterValues: tableFilterValues,
    setFilterValues: setTableFilterValues,
    disabledFilters: tableDisabledFilters,
    setDisabledFilters: setTableDisabledFilters,
    paginationState: tablePaginationState,
    setPaginationState: setTablePaginationState,
    highlightedRows,
    setHighlightedRows
  } = useTableState('DocumentsListTable', {
    defaultSortState: { columnKey: 'uploadedAt', direction: 'desc' }
  });
  const [ensureRowVisibleKey, setEnsureRowVisibleKey] = useState(null);
  const [columnCustomizationOpen, setColumnCustomizationOpen] = useState(false);
  const [assigneeModalOpen, setAssigneeModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [bulkRegenerateModalOpen, setBulkRegenerateModalOpen] = useState(false);
  const [bulkAssignModalOpen, setBulkAssignModalOpen] = useState(false);
  const [documentsForBulkAction, setDocumentsForBulkAction] = useState([]);
  const [bulkRegenerateLoading, setBulkRegenerateLoading] = useState(false);
  const [bulkAssignLoading, setBulkAssignLoading] = useState(false);

  // Returning from the detail page lands here with the document to highlight and scroll to
  useEffect(() => {
    const highlightDocumentId = location.state?.highlightDocumentId;
    if (highlightDocumentId) {
      setHighlightedRows(new Set([highlightDocumentId]));
      setEnsureRowVisibleKey(highlightDocumentId);

      // Clear the location state to prevent re-highlighting on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state, setHighlightedRows]);

  // The table's current filtered and sorted row order, which is what prev/next pages through
  const filteredDocIdsRef = useRef([]);
  const handleFilteredSortedDataChange = useCallback((filteredSortedDocs) => {
    if (!Array.isArray(filteredSortedDocs)) return;
    filteredDocIdsRef.current = filteredSortedDocs.map(doc => doc.documentId);
  }, []);

  const openDocument = useCallback((doc) => {
    saveDocumentNavigation(filteredDocIdsRef.current, doc.documentId);
    navigate(`/document-detail/${doc.documentId}`);
  }, [navigate]);

  const clearAssigneeFilter = useCallback(() => {
    setTableFilterValues(prev => {
      const newFilters = { ...prev };
      delete newFilters.assignee;
      return newFilters;
    });
    setTableDisabledFilters(prev => {
      const newSet = new Set(prev);
      newSet.delete('assignee');
      return newSet;
    });
  }, [setTableFilterValues, setTableDisabledFilters]);

  const handleAssigneeFilterClick = useCallback((assigneeName) => {
    if (!assigneeName) {
      return;
    }
    setTableFilterValues(prev => ({
      ...prev,
      assignee: assigneeName
    }));
    setTableDisabledFilters(prev => {
      const newSet = new Set(prev);
      newSet.delete('assignee');
      return newSet;
    });
  }, [setTableFilterValues, setTableDisabledFilters]);

  const columns = useMemo(() => [
    // Risk Category Column
    createEnumColumn(
      'riskCategory',
      'Urgency',
      (doc) => doc.riskCategory,
      ['LOW', 'MEDIUM', 'HIGH'],
      {
        compulsory: true,
        renderer: (value) => {
          const riskCategory = value ? RiskCategoryEnum[value] : null;
          return riskCategory ? (
            <StatusBadge
              label={riskCategory.displayName}
              colorFamily={riskCategory.colorFamily || 'gray'}
              size="normal"
              className="risk-category-badge"
              tooltip={(
                <span className="risk-category-tooltip-lead">
                  <FontAwesomeIcon icon={riskCategory.icon} />
                  {riskCategory.description}
                </span>
              )}
            />
          ) : null;
        },
        sortable: true,
        compareFn: (a, b) => {
          // Define logical risk category ordering: LOW (0) → MEDIUM (1) → HIGH (2)
          const riskOrder = { 'LOW': 0, 'MEDIUM': 1, 'HIGH': 2 };

          const aRiskValue = a.riskCategory || '';
          const bRiskValue = b.riskCategory || '';

          // Handle null/undefined values (put them at the end)
          if (!aRiskValue && !bRiskValue) return 0;
          if (!aRiskValue) return 1;
          if (!bRiskValue) return -1;

          const aOrder = riskOrder[aRiskValue] ?? 999; // Unknown values go to end
          const bOrder = riskOrder[bRiskValue] ?? 999;

          return aOrder - bOrder;
        }
      }
    ),

    // Document Name Column (now shows patient name)
    createTextColumn(
      'name',
      'Patient Name',
      (doc) => {
        // First try to get patient data from the persistent map using nhsId
        if (doc.nhsId && nhsIdToPatientData.has(doc.nhsId)) {
          const patientData = nhsIdToPatientData.get(doc.nhsId);
          return patientData?.name || patientData?.fullName || 'Unknown';
        }
        // Fallback to existing patient data in document
        return doc.patient?.name || 'Unknown';
      },
      { filterable: false, sortable: false }
    ),

    // Patient Column (now shows NHS ID)
    createTextColumn(
      'patient',
      'NHS Number',
      (doc) => {
        if (!doc.nhsId) return 'Unknown';
        // Format NHS ID as XXX XXX XXXX
        const nhsId = doc.nhsId.replace(/\s/g, ''); // Remove any existing spaces
        if (nhsId.length === 10) {
          return `${nhsId.slice(0, 3)} ${nhsId.slice(3, 6)} ${nhsId.slice(6, 10)}`;
        }
        return doc.nhsId; // Return original if not 10 digits
      },
      { filterable: false, sortable: false }
    ),

    // Status column: READY is shown as In Review / Assigned / Ready depending on viewers and
    // assignee, and that display value is what filtering, sorting and rendering all use.
    (() => {
      const getDisplayStatusValue = (doc) => {
        const rawStatus = doc.status;

        if (rawStatus === StatusEnum.READY.value) {
          const docHasViewers = hasViewers && hasViewers(doc.documentId);

          if (docHasViewers) {
            return StatusEnum.IN_REVIEW.value;
          } else if (outOfOfficeForAssignee(doc.assigneeId, activeUsers)) {
            return StatusEnum.ASSIGNED_TO_OOO.value;
          } else if (doc.assigneeId) {
            return StatusEnum.ASSIGNED.value;
          } else {
            return StatusEnum.READY.value;
          }
        } else {
          return rawStatus;
        }
      };

      return createEnumColumn(
        'status',
        'Status',
        (doc) => getDisplayStatusValue(doc),
        Object.keys(StatusEnum),
        {
          compulsory: true,
          renderer: (value, doc) => {
            const displayStatus = StatusEnum[value];

            return (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <StatusBadge
                  label={displayStatus?.description || value}
                  colorFamily={displayStatus?.colorFamily || 'gray'}
                  size="normal"
                />
                <NotificationPreview
                  comments={doc?.comments || []}
                  violations={doc?.lmcContractViolations || []}
                  isMultiplePatients={doc?.isMultiplePatients === true}
                  activeUsers={activeUsers}
                />
              </div>
            );
          },
          sortable: true,
          compareFn: (a, b) => {
            const aStatusValue = getDisplayStatusValue(a);
            const bStatusValue = getDisplayStatusValue(b);
            const aStatus = StatusEnum[aStatusValue]?.description || aStatusValue || '';
            const bStatus = StatusEnum[bStatusValue]?.description || bStatusValue || '';
            return aStatus.localeCompare(bStatus);
          }
        }
      );
    })(),

    // Assignee Column
    createTextColumn(
      'assignee',
      'Assignee',
      (doc) => getAssigneeName(doc.assigneeId),
      {
        renderer: (value, doc) => (
          <span className="assignee-with-availability">
            <span>{value}</span>
            <OutOfOfficeMark outOfOffice={outOfOfficeForAssignee(doc.assigneeId, activeUsers)} />
          </span>
        ),
      }
    ),

    // Letter Type Column
    createTextColumn(
      'letterType',
      'Letter Type',
      (doc) => {
        const terms = doc.documentDetails?.letterTypeCode?.terms;
        if (Array.isArray(terms) && terms.length > 0 && terms[0]) {
          return String(terms[0]);
        }
        return '';
      },
      { sortable: true }
    ),

    // Uploaded at Column
    createDateColumn(
      'uploadedAt',
      'Uploaded at',
      (doc) => doc.timestamp,
      { compulsory: true }
    ),

    // Actions Column
    createCustomColumn(
      'actions',
      'Actions',
      (doc) => doc,
      (value, doc) => {
        const isFiledAway = doc.status === StatusEnum.FILED_AWAY.value;
        const isInvalid = doc.status === StatusEnum.INVALID.value;
        const { canSplit, canRegenerate, canDelete } = getRowActions(doc);

        return (
          <div className="actions-row">
            <ActionButton
              icon={faEye}
              onClick={() => openPreview(doc.documentId)}
              disabled={false}
              title="Preview Document"
            />
            <ActionButton
              icon={faScissors}
              onClick={() => handleSplitExistingDocument(doc)}
              disabled={!canSplit}
              title={canSplit ? "Split into multiple documents" : "Splitting is only available for ready PDFs"}
            />
            <ActionButton
              icon={faRepeat}
              onClick={() => handleRegenerateClick(doc.documentId)}
              disabled={!canRegenerate}
              title={isFiledAway ? "Filed Away - Regeneration Disabled" : "Regenerate Document"}
            />
            <ActionButton
              icon={faTrash}
              onClick={() => handleDeleteClick(doc.documentId)}
              disabled={!canDelete}
              title={isFiledAway ? "Delete Filed Away Document" : isInvalid ? "Delete Invalid Document" : "Delete Document"}
            />
          </div>
        );
      },
      {
        compulsory: true,
        sortable: false,
        filterable: false,
        className: 'actions-column'
      }
    ),

    // Optional columns (not in default view)

    // Organisation Column
    createCustomColumn(
      'organisation',
      'Organisation',
      (doc) => doc.documentDetails?.sender?.organisation,
      (value) => {
        if (value) {
          const parts = value.split(',');
          return parts.slice(0, 3).join(', ');
        }
        return 'Unknown';
      }
    ),

    // Document Title Column
    createTextColumn(
      'documentTitle',
      'Document Title',
      (doc) => doc.overrideTitle || doc.documentTitle || 'Untitled'
    ),

    // Page Count Column
    createTextColumn(
      'pageCount',
      'Pages',
      (doc) => String(doc.pageCount || 0)
    ),

    // Letter Date Column (date only, no time)
    createDateColumn(
      'letterDate',
      'Letter Date',
      (doc) => doc.documentDetails?.date,
      {
        renderer: (value) => {
          if (!value) return 'Unknown';
          try {
            return new Date(value).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            });
          } catch {
            return value;
          }
        }
      }
    ),

    createEnumColumn(
      'documentSource',
      'Source',
      (doc) => resolveDocumentSource(doc),
      Object.keys(DocumentSourceEnum),
      {
        renderer: (value) => DocumentSourceEnum[value]?.displayName ?? value,
        sortable: true,
        compareFn: (a, b) => {
          const order = { MESH: 0, EMAIL: 1, MANUAL: 2 };
          const aVal = resolveDocumentSource(a);
          const bVal = resolveDocumentSource(b);
          return (order[aVal] ?? 99) - (order[bVal] ?? 99);
        },
      }
    ),

    // Filed away at Column
    createDateColumn(
      'filedAwayAt',
      'Filed away at',
      (doc) => doc.filedAwayAt
    )
  ], [handleRegenerateClick, handleDeleteClick, handleSplitExistingDocument, openPreview, getAssigneeName, activeUsers, nhsIdToPatientData, hasViewers]);

  const { columnOrder, hiddenColumns, saveSettings } = useColumnSettings(
    'DocumentsListTable', columns, DOCUMENTS_TABLE_DEFAULT_HIDDEN_COLUMNS);
  const hiddenColumnsSet = useMemo(() => new Set(hiddenColumns), [hiddenColumns]);

  const openBulkModal = (setOpen) => (selectedRowsData) => {
    if (selectedRowsData.length === 0) return;
    setDocumentsForBulkAction(selectedRowsData);
    setOpen(true);
  };

  const bulkActions = [
    {
      key: 'regenerate',
      label: 'Regenerate',
      icon: faRepeat,
      onClick: openBulkModal(setBulkRegenerateModalOpen)
    },
    {
      key: 'assign',
      label: 'Assign',
      icon: faUserCheck,
      onClick: openBulkModal(setBulkAssignModalOpen)
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: faTrash,
      onClick: openBulkModal(setBulkDeleteModalOpen)
    }
  ];

  const closeBulkModal = () => {
    setBulkDeleteModalOpen(false);
    setBulkRegenerateModalOpen(false);
    setBulkAssignModalOpen(false);
    setDocumentsForBulkAction([]);
  };

  const confirmBulkRegenerate = () => {
    if (documentsForBulkAction.length === 0) return;

    setBulkRegenerateLoading(true);

    // HTTP_IMMEDIATE strategy: SSE uses SYSTEM_USER_ID so would be filtered out
    const regeneratePromises = documentsForBulkAction.map(doc =>
      documentService.regenerateDocument(doc.documentId)
        .then(() => {
          addNotification(`bulk-regenerate-success-${doc.documentId}`, `Queued for regeneration: ${doc.documentTitle || doc.documentId}`);
        })
        .catch(error => {
          console.error(`Error regenerating document ${doc.documentId}:`, error);
          addNotification(`bulk-regenerate-error-${doc.documentId}`, `Failed to regenerate document: ${doc.documentTitle || doc.documentId}`, { isError: true });
        })
    );

    Promise.allSettled(regeneratePromises).then(() => {
      setSelectedRows(new Set());
      setBulkRegenerateModalOpen(false);
      setDocumentsForBulkAction([]);
    }).finally(() => {
      setBulkRegenerateLoading(false);
    });
  };

  const confirmBulkDelete = () => {
    if (documentsForBulkAction.length === 0) return;

    const deletePromises = documentsForBulkAction.map(doc =>
      documentService.deleteDocument(doc.documentId)
        .catch(error => {
          console.error(`Error deleting document ${doc.documentId}:`, error);
          addNotification(`bulk-delete-error-${doc.documentId}`, `Failed to delete document: ${doc.documentTitle || doc.documentId}`, { isError: true });
        })
    );

    Promise.allSettled(deletePromises).then(() => {
      refreshDocuments();
      setSelectedRows(new Set());
      setBulkDeleteModalOpen(false);
      setDocumentsForBulkAction([]);
    });
  };

  const confirmBulkAssign = (assigneeId) => {
    if (documentsForBulkAction.length === 0) return;

    setBulkAssignLoading(true);

    const assignPromises = documentsForBulkAction.map(doc =>
      documentService.updateDocumentAssignee(doc.documentId, assigneeId)
        .catch(error => {
          console.error(`Error assigning document ${doc.documentId}:`, error);
          addNotification(`bulk-assign-error-${doc.documentId}`, `Failed to assign document: ${doc.documentTitle || doc.documentId}`, { isError: true });
        })
    );

    Promise.allSettled(assignPromises).then(() => {
      refreshDocuments();
      setSelectedRows(new Set());
      setBulkAssignModalOpen(false);
      setDocumentsForBulkAction([]);
    }).finally(() => {
      setBulkAssignLoading(false);
    });
  };

  return {
    columns,
    columnOrder,
    hiddenColumnsSet,
    saveSettings,
    defaultHidden: DOCUMENTS_TABLE_DEFAULT_HIDDEN_COLUMNS,
    columnCustomizationOpen,
    setColumnCustomizationOpen,
    tableSortState,
    setTableSortState,
    tableFilterValues,
    setTableFilterValues,
    tableDisabledFilters,
    setTableDisabledFilters,
    tablePaginationState,
    setTablePaginationState,
    highlightedRows,
    setHighlightedRows,
    ensureRowVisibleKey,
    handleFilteredSortedDataChange,
    openDocument,
    clearAssigneeFilter,
    handleAssigneeFilterClick,
    assigneeModalOpen,
    setAssigneeModalOpen,
    selectedRows,
    setSelectedRows,
    bulkActions,
    documentsForBulkAction,
    bulkDeleteModalOpen,
    bulkRegenerateModalOpen,
    bulkAssignModalOpen,
    bulkRegenerateLoading,
    bulkAssignLoading,
    closeBulkModal,
    confirmBulkRegenerate,
    confirmBulkDelete,
    confirmBulkAssign,
  };
};

export default useDocumentTable;
